import { Image, ImageProps } from '@unistocks/components/basic/image';
import { useAssetImage } from '@unistocks/features/assets/hooks/use-asset-image';
import { AssetDto, AssetType, CalculatedValueResultDto } from '@unistocks/proxy/axios-client';
import { GetProps, ListItem, styled, withStaticProperties } from 'tamagui';

const StyledListItem = styled(ListItem, {
  name: 'ValueListItem',
  render: 'div',
  size: '$3',
  gap: '$2',
  p: 0,
  cursor: 'inherit',

  hoverStyle: {
    backgroundColor: 'transparent',
    borderColor: '$borderColor'
  },

  pressStyle: {
    backgroundColor: 'transparent',
    borderColor: '$borderColor'
  }
});

const ListItemLabel = styled(ListItem.Text, {
  size: '$2',
  userSelect: 'none',
  color: '$bodyFont',
  ellipsis: false,
  flexGrow: 1,
  flexShrink: 1,
  numberOfLines: 2,

  '$platform-web': {
    textWrap: 'pretty',
    whiteSpace: 'normal'
  }
});

const ListItemValue = styled(ListItemLabel, {
  fontWeight: '$7',
  width: '100%',
  numberOfLines: 1,

  variants: {
    status: {
      neutral: {
        color: '$bodyFont'
      },
      positive: {
        color: '$positive'
      },
      negative: {
        color: '$negative'
      },
      stale: {
        color: '$gray9'
      }
    }
  } as const,

  defaultVariants: {
    status: 'neutral'
  }
});

export type ListItemImageProps = Omit<ImageProps, 'source'> & {
  calculatedValue?: CalculatedValueResultDto;
};

const ListItemImage: React.FC<ListItemImageProps> = ({ calculatedValue: item, style, ...rest }) => {
  const src = useAssetImage(
    {
      type: AssetType.Stock,
      isHoldings: true,
      market: item?.extraProperties['Market'],
      imageUri: item?.extraProperties['ImageUri']
    } as AssetDto,
    { preferAssetImage: true }
  );

  if (!src) return null;

  return <Image rounded source={src} {...rest} />;
};

export type ValueListItemStatus = GetProps<typeof ListItemValue>['status'];
export type ValueListItemProps = GetProps<typeof ValueListItem>;

export const ValueListItem = withStaticProperties(StyledListItem, {
  Label: ListItemLabel,
  Value: ListItemValue,
  Image: ListItemImage
});
